import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

export const GetSalaryDetails = async (
  employeeId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/salary-details`, {
      params: { employee_id: employeeId },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load salary details.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const UpdateSalaryDetails = async (
  payload: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/payroll/salary-details/update`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to save salary details.",
      errors: error?.response?.data?.errors,
      error: {
        code: error?.response?.status || 400,
        type: "validation",
      },
    };
  }
};

export const GetAdvances = async (params: {
  employee_id?: string;
  is_deducted?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/advances`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load salary advances.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreateAdvance = async (payload: {
  employee_id: string;
  amount: number;
  advance_date: string;
  notes?: string;
  payment_method_id?: string;
  payment_details?: any;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/payroll/advances/create`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to register salary advance.",
      errors: error?.response?.data?.errors,
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteAdvance = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/advances/delete`, {
      id,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to delete advance.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetPayrolls = async (params: {
  month?: string;
  search?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load payroll registers.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetPayrollDetail = async (
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/detail`, {
      params: { id },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load payroll details.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GeneratePayrollPreview = async (params: {
  employee_id: string;
  payroll_month: string;
  ot_hours?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/generate`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to calculate payroll preview.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const SavePayroll = async (payload: any): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/save`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to save payroll record.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const PayPayroll = async (payload: {
  id: string;
  payment_method: string;
  payment_method_id?: string;
  payment_details?: any;
  payment_note?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/pay`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to mark payroll as paid.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeletePayroll = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/delete`, { id });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to delete payroll record.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetTenantPayrollPolicy = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/policy`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load tenant payroll policy.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const UpdateTenantPayrollPolicy = async (
  payload: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/policy/update`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to save tenant payroll policy.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetPromotionHistory = async (
  employeeId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/promotions/history`, {
      params: { employee_id: employeeId },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load promotion history.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreatePromotionIncrement = async (
  payload: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/payroll/promotions/create`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to log promotion / salary increment.",
      errors: error?.response?.data?.errors,
      error: {
        code: error?.response?.status || 400,
        type: "validation",
      },
    };
  }
};

export const DeletePromotionIncrement = async (
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/promotions/delete`, {
      id,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to delete promotion record.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetShifts = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/shifts`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load employee shifts.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const SaveShift = async (payload: any): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/shifts/save`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to save work shift.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteShift = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/shifts/delete`, { id });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to delete shift.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetEmployeeShiftHistory = async (
  employeeId?: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/payroll/shifts/assignments`, {
      params: { employee_id: employeeId },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load employee shift assignment history.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const AssignEmployeeShift = async (
  payload: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/shifts/assign`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to assign work shift.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const ToggleShiftAssignmentStatus = async (
  payload: { id: string; is_active: number },
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/payroll/shifts/assignment/toggle-status`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to update shift assignment status.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};



